/**
 * Typed client for the OptionEngine FastAPI backend.
 *
 * Every interface here mirrors a serialiser in the Python layer — keep them in
 * step:
 *   Quote     <- app.quote_dict()
 *   Trade     <- models.Trade.to_dict()
 *   Signal    <- engine._sig_dict()
 *   Decision  <- engine.Decision.to_dict()
 *   BotStats  <- bot.RoboBot.stats()
 */

export type Direction = "LONG" | "SHORT" | "FLAT";
export type Regime = "TRENDING" | "BREAKOUT" | "RANGING" | "VOLATILE";
export type BotMode = "LIVE" | "OFF";
export type ExitReason = "TARGET" | "STOP" | "EOD" | "MANUAL" | "";

export interface Quote {
  symbol: string;
  label: string;
  exchange: string;
  ltp: number;
  prev_close: number;
  change: number;
  change_pct: number;
  lot_size: number;
}

export interface Trade {
  id: string;
  symbol: string;
  instrument: string;
  option: "CE" | "PE";
  strike: number;
  direction: Direction;
  strategy: string;
  strategy_label: string;
  reason: string;
  qty: number;
  lots: number;
  entry_price: number;
  entry_underlying: number;
  entry_ts: number;
  target_underlying: number;
  stop_underlying: number;
  ltp: number;
  status: "OPEN" | "CLOSED";
  exit_price: number;
  exit_ts: number;
  exit_reason: ExitReason;
  pnl: number;
  pnl_pct: number;
}

export interface Signal {
  strategy: string;
  strategy_label: string;
  direction: Direction;
  confidence: number;
  regime_fit: number;
  score: number;
  /**
   * Scored by the engine but barred from firing (prebuilt.SHADOW_BY_DEFAULT).
   * A shadow signal is a real, current opinion with no position behind it —
   * render it so it can never be mistaken for one that traded.
   */
  observe_only?: boolean;
  reason: string;
  ref_price: number;
  target: number;
  stop: number;
  meta: Record<string, number | string | boolean>;
}

export interface RegimeMeta {
  adx: number;
  atr_pct: number;
  slope: number;
  near_breakout: boolean;
}

export interface Decision {
  symbol: string;
  regime: Regime;
  regime_meta: RegimeMeta;
  fired: Signal | null;
  signals: Signal[];
}

export interface BotStats {
  mode: BotMode;
  scans: number;
  started_at: number;
  open_count: number;
  closed_count: number;
  realised_pnl: number;
  unrealised_pnl: number;
  total_pnl: number;
  win_rate: number;
  wins: number;
  losses: number;
}

/** brokers.base.Broker.describe() — which adapter is serving this data. */
export interface BrokerInfo {
  name: string;
  connected: boolean;
  capabilities: string[];
  live_orders: boolean;
  /**
   * True when the feed's clock does not advance — the cached-history fallback
   * used while no broker is reachable. Prices are REAL but frozen at the last
   * cached bar, and the bot is not started, so nothing can trade.
   */
  offline?: boolean;
  /** ISO timestamp of the last real bar, when offline. */
  last_bar?: string;
  /** Sessions of history on disk, when offline. */
  sessions?: number;
  /** Human explanation from the adapter; prefer it over any hardcoded blurb. */
  note?: string;
}

export interface DashboardPayload {
  clock: string;
  mode: BotMode;
  broker: BrokerInfo;
  stats: BotStats;
  indices: Quote[];
  ticker: Quote[];
  today: Trade[];
  closed: Trade[];
  decisions: Record<string, Decision>;
  /** OE_DETACH_OTHERS on the server: only CAS Engine and Web + Elliott run. */
  detached?: boolean;
}

export interface StrategyPayload {
  quote: Quote;
  decision: Decision;
  open_trade: Trade | null;
}

export interface McxPayload {
  clock: string;
  mode: BotMode;
  prices: Quote[];
  strategies: Record<string, StrategyPayload>;
}

export interface Candle {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

/* --- pre-built strategies (the live-paper umbrella) ----------------------- */

export interface PrebuiltToday {
  realised: number;
  unrealised: number;
  closed: number;
  active: number;
}

export interface PrebuiltStrategy {
  key: string;
  label: string;
  path: string;
  status: string;
  error: string;
  market_open: boolean;
  symbols: string[];
  timeframe: string;
  period: string;
  today: PrebuiltToday;
}

export interface PrebuiltPayload {
  strategies: PrebuiltStrategy[];
}

/* --- backtest ------------------------------------------------------------ */

export interface BacktestTrade {
  id: string;
  symbol: string;
  instrument: string;
  option: "CE" | "PE";
  strike: number;
  direction: Direction;
  strategy_label: string;
  reason: string;
  qty: number;
  entry_price: number;
  exit_price: number;
  entry_ts: number;
  exit_ts: number;
  exit_reason: ExitReason;
  pnl: number;
  pnl_pct: number;
}

export interface EquityPoint {
  date: string;
  equity: number;
  realised: number;
  open: number;
}

export interface Bucket {
  trades: number;
  pnl: number;
  wins: number;
}

export interface BacktestResult {
  start: string;
  end: string;
  trading_days: number;
  symbols: string[];
  trades: BacktestTrade[];
  equity: EquityPoint[];
  total_pnl: number;
  gross_profit: number;
  gross_loss: number;
  profit_factor: number;
  win_rate: number;
  wins: number;
  losses: number;
  max_drawdown: number;
  max_drawdown_pct: number;
  avg_win: number;
  avg_loss: number;
  expectancy: number;
  costs_paid: number;
  by_strategy: Record<string, Bucket>;
  by_symbol: Record<string, Bucket>;
  exits: Record<string, number>;
}

export interface BacktestState {
  status: "idle" | "running" | "done" | "error";
  result: BacktestResult | null;
  error: string | null;
  params: { symbols: string[]; lots: number; warmup: number; costs: number } | null;
}

export interface PeriodOption {
  key: string;
  label: string;
  calendar_days: number;
  /** Trading days actually REPLAYED, i.e. after indicator warmup. */
  trading_days: number;
  /** Total cached days in the window, before warmup is consumed. */
  window_days: number;
  warmup_days: number;
  from: string | null;
  to: string | null;
  /** False only when the window is genuinely empty — a thin window still runs. */
  available: boolean;
  /** True when the sample is small enough to caution about. */
  thin?: boolean;
  note?: string;
}

export interface DataCoverage {
  trading_days: number;
  from: string | null;
  to: string | null;
  nse_days: number;
  bse_days: number;
  periods: PeriodOption[];
}

/* --- transport ----------------------------------------------------------- */

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { Accept: "application/json" },
    ...init,
  });
  if (!res.ok) {
    throw new Error(`${init?.method ?? "GET"} ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

const post = <T>(path: string) => req<T>(path, { method: "POST" });

/* --- Jarvis -------------------------------------------------------------- */

/** One Jarvis turn. ``audio`` is a base64 MP3 in the Hindi voice, or "". */
export interface JarvisReply {
  reply: string;
  audio: string;
}

/**
 * POST /api/jarvis. Unlike req(), this keeps the server's error detail —
 * "GEMINI_API_KEY is not set" is the message the page has to show, and a bare
 * "failed: 400" would hide it.
 */
export async function jarvisAsk(body: {
  session: string;
  text?: string;
  audio?: string;
  mime?: string;
  voice?: boolean;
}): Promise<JarvisReply> {
  const res = await fetch("/api/jarvis", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `Jarvis failed: ${res.status}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = String(j.detail);
    } catch {
      /* non-JSON error page (e.g. Cloudflare) -- keep the status line */
    }
    throw new Error(detail);
  }
  return (await res.json()) as JarvisReply;
}

export const jarvisReset = (session: string) =>
  req<{ ok: boolean }>(`/api/jarvis/reset?session=${encodeURIComponent(session)}`,
    { method: "POST" });

/** One entry in the MasterOption-Strategy catalogue. */
export interface MasterStrategy {
  key: string;
  name: string;
  outlook: string;      // BULLISH / BEARISH / NEUTRAL / VOLATILE
  volatility: string;   // LONG_VOL / SHORT_VOL / NEUTRAL
  proficiency: string;  // NOVICE / INTERMEDIATE / ADVANCED / EXPERT
  risk: string;         // LIMITED / UNLIMITED
  reward: string;
  margin: string;       // LOW / MEDIUM / HIGH — SPAN+exposure burden in India
  family: string;
  summary: string;
  /** What changes about this strategy in the Indian market. */
  india: string;
  legs: string[];
  leg_count: number;
  /** True when a leg needs the underlying — futures, for an index. */
  needs_futures: boolean;
  net_debit: boolean | null;
  /** Estimated rupees blocked to carry one lot on NIFTY. */
  capital: number;
}

export interface MasterCatalog {
  total: number;
  shown: number;
  strategies: MasterStrategy[];
  facets: Record<string, Record<string, number>>;
}

export interface MasterDetail extends MasterStrategy {
  symbol: string;
  atm: number;
  lot: number;
  step: number;
  spots: number[];
  payoff: number[];
  net_cost: number;
  max_profit: number;
  max_loss: number;
  breakevens: number[];
  premiums: number[];
  profit_capped: boolean;
  loss_capped: boolean;
}

export interface MasterBacktestRow {
  key: string;
  name: string;
  outlook: string;
  margin: string;
  risk: string;
  family: string;
  cycles: number;
  net: number;
  /** Estimated capital blocked to carry one lot — SPAN scan + exposure. */
  capital: number;
  /** Cumulative net P&L as a % of capital, over the whole window. */
  roc_pct: number;
  /** Per-year return — the only figure comparable across different windows. */
  roc_annual_pct: number;
  /** True when a futures leg is present, so the P&L includes index drift. */
  has_futures: boolean;
  /** P&L attributed to the call legs — shows which side carried the trade. */
  ce_pnl: number;
  pe_pnl: number;
  fut_pnl: number;
  wins: number;
  win_rate: number;
  avg: number;
  best: number;
  worst: number;
  profit_factor: number;
}

export interface MasterBacktest {
  symbol: string;
  period: string;
  cycle: string;
  hold_sessions: number;
  sessions: number;
  cycles: number;
  from: string;
  to: string;
  profitable: number;
  total: number;
  years: number;
  /** 1 lot of the index held for the whole window — the bar to beat. */
  buy_and_hold: number;
  beat_buy_and_hold: number;
  /** True when fewer than 10 cycles ran — treat the ranking as indicative. */
  thin: boolean;
  results: MasterBacktestRow[];
}

export interface MasterBacktestState {
  status: "idle" | "running" | "done" | "error";
  result: MasterBacktest | null;
  error: string | null;
  params: Record<string, string>;
}

export interface TMPreset {
  key: string;
  name: string;
  entry_tf: string;
  confirm_tf: string;
  strike: string;
  stop_pct: number;
  target_pct: number;
  st: string;
  adx_min: number;
  max_trades_day: number;
  risk_pct: number;
  capital: number;
  pullback: boolean;
  engulfing: boolean;
}

export interface TMCoverage {
  symbol: string;
  bars: number;
  days: number;
  from: string | null;
  to: string | null;
  /** False when too few 1-minute bars are cached to run at all. */
  ready: boolean;
}

export interface TMRow {
  preset: string;
  name: string;
  symbol: string;
  entry_tf: string;
  confirm_tf: string;
  trades: number;
  net: number;
  gross: number;
  costs: number;
  wins: number;
  losses: number;
  win_rate: number;
  profit_factor: number;
  avg_win: number;
  avg_loss: number;
  max_drawdown: number;
  capital: number;
  roc_pct: number;
  exits: Record<string, number>;
}

export interface TMState {
  status: "idle" | "running" | "done" | "error";
  result: {
    rows: TMRow[];
    symbols: string[];
    presets: string[];
    days: number;
    from: string | null;
    to: string | null;
    sessions: number;
    /** Smallest trade count across the run — the honest sample size. */
    min_trades: number;
    /** True when any combination produced under 20 trades. */
    thin: boolean;
    profitable: number;
    total: number;
  } | null;
  error: string | null;
}

export interface STMRow {
  symbol: string;
  lot: number;
  step: number;
  last_price: number;
  trades: number;
  net: number;
  gross: number;
  costs: number;
  win_rate: number;
  profit_factor: number;
  max_drawdown: number;
  roc_pct: number;
}

export interface STMUniverse {
  universe: {
    symbol: string; lot: number; bars: number; days: number;
    from: string | null; to: string | null; ready: boolean;
    volatile: boolean; step: number; last: number;
  }[];
  ready: number;
  total: number;
  presets: { key: string; name: string; entry_tf: string; confirm_tf: string }[];
}

export interface STMState {
  status: "idle" | "running" | "done" | "error";
  result: any;
  error: string | null;
}

export interface ISWMeta {
  setups: { key: string; description: string }[];
  symbols: string[];
  timeframes: number[];
}

export interface ISWState {
  status: "idle" | "running" | "done" | "error";
  result: any;
  error: string | null;
}

/* --- stock playbook ------------------------------------------------------ */

/** One NIFTY-50 F&O name and what history exists for it. */
export interface PlaybookStock {
  symbol: string;
  lot: number;
  sessions: number;
  has_data: boolean;
  high_volatility: boolean;
}

/** Anything assignable: an option structure, a price setup, or the trend signal. */
export interface PlaybookStrategy {
  key: string;
  name: string;
  kind: "structure" | "setup" | "directional";
  outlook: string;
  risk: string;
  legs: number;
  summary: string;
  source?: string;
  family?: string;
  needs_futures?: boolean;
}

/** A live row: the assignment plus whatever pricing or signal it produces. */
export interface PlaybookRow {
  symbol: string;
  strategy: string;
  name?: string;
  kind?: "structure" | "setup" | "directional";
  timeframe: string;
  lots: number;
  enabled: boolean;
  spot?: number;
  error?: string;
  /* structures */
  max_loss?: number;
  max_profit?: number;
  rr?: number;
  margin?: number;
  cost_drag?: number;
  contract_value?: number;
  physical_settlement_risk?: boolean;
  legs?: string[];
  /* setups */
  direction?: string;
  entry?: number | null;
  stop?: number | null;
  reason?: string;
  risk_points?: number;
  risk_underlying?: number;
  bars?: number;
}

/** One playbook trade in the per-symbol drill-down. */
export interface PlaybookTrade {
  t: number;
  direction: "LONG" | "SHORT";
  entry: number;
  exit: number;
  stop: number;
  points: number;
  pnl: number;
  bars_held: number;
  reason: string;
}

/** One symbol's offline replay of its assigned setup. */
export interface PlaybookBacktestRow {
  symbol: string;
  strategy: string;
  name?: string;
  timeframe: string;
  bars: number;
  trades: number;
  net: number;
  net_points?: number;
  wins: number;
  losses: number;
  win_rate: number;
  profit_factor: number;
  avg: number;
  best: number;
  worst: number;
  stops?: number;
  times?: number;
  span?: string;
  period_days?: number;
  lot: number;
  error?: string;
  // present only on the single-symbol drill-down
  single?: boolean;
  trades_list?: PlaybookTrade[];
  trades_capped?: boolean;
  equity?: number[];
  equity_points?: number[];
}

export interface PlaybookBacktestState {
  status: "idle" | "running" | "done" | "error";
  error: string | null;
  result: ({
    // book (all-assignments) shape
    results?: PlaybookBacktestRow[];
    skipped?: { symbol: string; strategy: string; why: string }[];
    max_hold?: number;
    period_days?: number;
    total_net?: number;
    total_points?: number;
    total_trades?: number;
    profitable?: number;
    tested?: number;
  } & Partial<PlaybookBacktestRow>) | null;   // single-symbol drill-down merges the row
}

export interface PlaybookState {
  assignments: PlaybookRow[];
  count: number;
  enabled: number;
  total_max_loss: number;
  total_margin: number;
  total_contract_value: number;
  timeframes: { key: string; minutes: number; label: string }[];
  default_timeframe: string;
  universe: PlaybookStock[];
  strategies: PlaybookStrategy[];
}

/* --- index / sector playbook --------------------------------------------- */

/** One tradable index/sector derivative in the option playbook universe. */
export interface PlaybookIndex {
  symbol: string;
  name: string;
  sector: string;
  exchange: string;
  lot: number;
  step: number;
  settle: string;
  expiry: string;
}

/** Index playbook state — cash settled, so no contract-value total. */
export interface IndexPlaybookState {
  assignments: PlaybookRow[];
  count: number;
  enabled: number;
  total_max_loss: number;
  total_margin: number;
  settle: string;
  timeframes: { key: string; minutes: number; label: string }[];
  default_timeframe: string;
  universe: PlaybookIndex[];
  strategies: PlaybookStrategy[];
}

// ---- Closing Auction Session ----------------------------------------------
//
// Mirrors backend/cas: schedule.CasClock.to_dict, forecast.Forecast.to_dict,
// expiry.CasExpiryEngine.report.

export type CasPhase =
  | "PRE" | "REFERENCE" | "FREEZE" | "ENTRY_1" | "ENTRY_2"
  | "MATCH" | "POST" | "DONE";

/** AUCTION_LIVE = the feed is showing auction prices; FROZEN = 15:15 prints. */
export type CasFeedMode = "AUCTION_LIVE" | "PARTIAL" | "FROZEN" | "UNKNOWN";

export interface CasClock {
  now: string;
  phase: CasPhase;
  phase_help: string;
  in_auction: boolean;
  orders_open: boolean;
  options_open: boolean;
  /** 0 at 15:20 -> 1 at the 15:28 random cutoff. */
  resolution: number;
  seconds_to_match: number;
  freeze: string;
  entry1: string;
  entry2: string;
  random_from: string;
  match: string;
  post: string;
  deriv_close: string;
}

export interface CasContributor {
  symbol: string;
  weight: number;
  freeze: number;
  auction: number;
  pct: number;
  points: number;
  moved: boolean;
}

export interface CasForecast {
  index: string;
  phase: CasPhase;
  ok: boolean;
  reason: string;
  /** The 15:15 anchor — the flat line a chart shows during the auction. */
  freeze_level: number;
  live_level: number;
  /** Where CAS is heading. This is the number that settles, not live_level. */
  predicted: number;
  move: number;
  sigma: number;
  band68: [number, number];
  band95: [number, number];
  resolution: number;
  coverage: number;
  resolved: number;
  feed_mode: CasFeedMode;
  sigma_parts: {
    tracking: number; unresolved: number; drift: number;
    /** Learned correction applied to the combined sigma; 1.0 until measured. */
    scale: number;
  };
  contributors: CasContributor[];
  calibration: CasCalibration | null;
  /** Non-zero once the official close is stamped. */
  stamped: number;
  /** Index future LTP — trades to 15:40, never freezes. */
  future: number;
  /** median(future − spot) measured before the 15:15 freeze. */
  basis: number;
  /** future − basis: an implied close available while the cash feed is frozen. */
  futures_implied: number;
  futures_move: number;
  /** First snapshot of the day (~09:15) -- a separate anchor from freeze_level. */
  open_level: number;
  /** Constituent point total since market open. NOT the CAS forecast --
   *  `move` above (anchored to the 15:15 freeze) is what `predicted` is
   *  built from. This is the dashboard's own running total. */
  day_move: number;
  samples?: number;
}

export interface CasCalibration {
  index: string;
  rms: number;
  worst: number;
  samples: number;
  source: "fit" | "prior";
  day: string;
  note: string;
  names: number;
  weights: Record<string, number>;
}

/** The COMMITTED prediction, pinned once at 15:25 and never revised. */
export interface CasCall {
  index: string;
  day: string;
  called: boolean;
  at?: string;
  predicted?: number;
  freeze?: number;
  move?: number;
  sigma?: number;
  lo?: number;
  hi?: number;
  feed_mode?: string;
  resolution?: number;
  coverage?: number;
  /** Present only once the official close has been stamped. */
  settled?: number;
  error?: number;
  naive_error?: number;
  beat_naive?: boolean;
  in_band?: boolean;
  /** % of the auction leg the call captured. */
  captured?: number | null;
}

export interface CasOverview {
  clock: CasClock;
  /** True when a simulator has taken over the clock — nothing on the page is real. */
  simulated: boolean;
  broker: string;
  phase_help: Record<string, string>;
  poller: {
    running: boolean;
    polls: number;
    errors: number;
    last_error: string;
    age_seconds: number | null;
    names: number;
    symbols_tracked: number;
  };
  indices: Record<string, CasForecast>;
  expiring_today: string[];
  calls: Record<string, CasCall>;
  call_time: string;
}

/** What the expiry engine's constituents-direction check found for one index
 *  at/after 15:14:30. `premium`/`tradingsymbol`/`lot_size` are only present
 *  once a contract was actually resolved and priced; `reject` explains why
 *  not when they're missing. */
/** One leg of the freeze-anchored guts (CALL @ freeze-100 or PUT @
 *  freeze+100). The expiry engine now opens both legs together, so a scan
 *  block carries an array of these rather than one. */
export interface ExpirySignal {
  kind: "CE" | "PE";
  strike: number;
  freeze?: number;
  premium?: number;
  tradingsymbol?: string;
  lot_size?: number;
  reject?: string;
}

export interface CasTrade {
  id: string;
  day: string;
  index: string;
  kind: "CE" | "PE";
  strike: number;
  tradingsymbol: string;
  lots: number;
  qty: number;
  entry_premium: number;
  entry_at: string;
  predicted: number;
  sigma: number;
  p_profit: number;
  ev_lot: number;
  resolution: number;
  feed_mode: CasFeedMode;
  freeze_level: number;
  status: "OPEN" | "SETTLED";
  settle_level: number;
  payoff: number;
  costs: number;
  pnl: number;
  exit_at: string;
  note: string;
}

export interface CasExpiryReport {
  config: {
    enabled: boolean;
    mode: "PAPER";
    strategy: string;
    entry: string;
    strike_offset: number;
    lots_per_leg: number;
    max_trades_per_day: number;
    indices: string[];
  };
  scans: number;
  open: CasTrade[];
  trades: CasTrade[];
  stats: {
    settled: number; wins: number; win_pct: number; pnl: number; avg: number;
  };
  /** {} until the first scan runs — check `last_scan.at`, not truthiness. */
  last_scan: CasScan | Record<string, never> | null;
}

export interface CasScan {
  at: string;
  phase: CasPhase;
  enabled: boolean;
  expiring: string[];
  action: string;
  indices: Record<string, {
    forecast: CasForecast;
    legs: ExpirySignal[] | null;
    gate: string;
    would_open?: ExpirySignal[];
    opened?: CasTrade[];
  }>;
}

export interface MomentumTrade {
  id: string; day: string; index: string; kind: "CE" | "PE"; strike: number;
  tradingsymbol: string; expiry: string; lots: number; qty: number;
  entry_premium: number; entry_ts: number; entry_at: string;
  day_move_at_entry: number; spot_at_entry: number;
  status: "OPEN" | "CLOSED";
  exit_premium: number; exit_at: string; pnl: number; note: string;
}

/** What the momentum engine's constituents-direction check found for one
 *  index. Mirrors ExpirySignal; `premium` present only once a contract was
 *  actually resolved and priced. */
export interface MomentumSignal {
  day_move: number;
  spot: number;
  kind: "CE" | "PE";
  strike: number;
  premium?: number;
  expiry?: string;
}

export interface MomentumScan {
  at: string;
  enabled: boolean;
  action?: string;
  indices: Record<string, {
    gate?: string;
    signal?: MomentumSignal;
    would_open?: MomentumSignal;
    opened?: MomentumTrade;
  }>;
}

export interface MomentumReport {
  config: {
    enabled: boolean;
    mode: "PAPER";
    entry_time: string;
    exit_target_points: Record<string, number>;
    lots_per_index: number;
    strike_offset: number;
    indices: string[];
  };
  scans: number;
  open: MomentumTrade[];
  trades: MomentumTrade[];
  stats: {
    closed: number; wins: number; win_pct: number; pnl: number; avg: number;
  };
  /** {} until the first scan runs — check `last_scan.at`, not truthiness. */
  last_scan: MomentumScan | Record<string, never>;
}

export interface MomentumCalendar {
  year: number;
  month: number;
  total_pnl: number;
  trade_count: number;
  day_count: number;
  /** Keyed by "YYYY-MM-DD"; a day absent here had no closed trades. */
  days: Record<string, { pnl: number; trades: number }>;
}

export interface CasAccuracy {
  days: number;
  mae?: number;
  /** MAE of the naive "close = freeze level" guess, to beat. */
  mae_naive_freeze?: number;
  within_1sigma_pct?: number;
  recent?: {
    day: string; freeze: number; close: number; move: number;
    predicted: number; sigma: number;
  }[];
}

export interface CasIndexDetail {
  clock: CasClock;
  forecast: CasForecast;
  accuracy: CasAccuracy;
  cas_sigma_pct: number;
  samples: number;
  constituents: string[];
}

/** Score breakdown for one option side (CE or PE) at one strike, from the
 *  NIFTY blast scanner. Fields are absent (not zero) when there was not yet
 *  enough rolling history to compute them. */
export interface BlastScore {
  score: number;
  rvol?: number;
  premium_z?: number;
  oi_change_pct?: number;
  iv_z?: number;
  underlying_z?: number;
  gamma?: number;
  above_vwap?: boolean;
  ltp?: number;
  delta?: number;
  iv?: number;
  oi?: number;
  rising?: boolean;
}

export interface BlastTrade {
  id: string;
  day: string;
  kind: "CE" | "PE";
  strike: number;
  tradingsymbol: string;
  lots: number;
  qty: number;
  entry_premium: number;
  entry_ts: number;
  entry_at: string;
  score_at_entry: number;
  components_at_entry: BlastScore;
  status: "OPEN" | "CLOSED";
  exit_premium: number;
  exit_at: string;
  pnl: number;
  note: string;
}

export interface BlastScan {
  at: string;
  spot: number;
  enabled: boolean;
  action?: string;
  strikes: Record<string, { ce?: BlastScore; pe?: BlastScore }>;
  best_ce?: BlastScore & { strike: number };
  best_pe?: BlastScore & { strike: number };
  would_open?: { kind: "CE" | "PE"; strike: number; score: number }[];
}

export interface BlastReport {
  config: {
    enabled: boolean;
    mode: "PAPER";
    symbol: string;
    entry_score: number;
    delta_range: [number, number];
    stop_loss_pct: number;
    target_pct: number;
    square_off_time: string;
    lots: number;
    poll_seconds: number;
    note: string;
  };
  scans: number;
  errors: number;
  last_error: string;
  open: BlastTrade[];
  trades: BlastTrade[];
  stats: {
    closed: number; wins: number; win_pct: number; pnl: number; avg: number;
  };
  last_scan: BlastScan | Record<string, never>;
}

// ---- WEB confluence + Elliott Wave (web_confluence.compute / elliott.analyze)

export interface WebNode {
  score: number | null;
  weight: number;
  available: boolean;
  detail: Record<string, unknown> | string;
}

export interface WebZone {
  lo: number;
  hi: number;
  mid: number;
  sources: string[];
  strength: number;
}

export interface WebPayload {
  error?: string;
  symbol: string;
  price: number;
  t: number;
  atr: number;
  score: number;
  bias: string;
  nodes: Record<string, WebNode>;
  time: { now: string; phase: string; trigger_window: boolean };
  zones_above: WebZone[];
  zones_below: WebZone[];
  trigger: null | {
    type: "BREAKOUT" | "REJECTION";
    direction: 1 | -1;
    zone: WebZone;
    stop: number;
    target: number;
    rr: number;
    valid: boolean;
  };
  options: {
    available: boolean;
    call_wall?: number;
    put_wall?: number;
    pcr_atm?: number | null;
    pcr_change?: number;
    atm_strike?: number;
    atm_iv?: { ce?: number; pe?: number };
    flow_events?: { strike: number; side: string; d_oi: number; d_ltp: number; read: string }[];
    flow_note?: string;
  };
  unavailable_inputs: string[];
  feed_offline: boolean;
}

export interface ElliottWaveLeg {
  label: string;
  from_price: number;
  to_price: number;
  from_t: number;
  to_t: number;
  running: boolean;
}

export interface ElliottScenario {
  scenario: "impulse" | "correction";
  current_wave: string;
  trend: "UP" | "DOWN";
  waves: ElliottWaveLeg[];
  fit: number;
  notes: string[];
  targets: Record<string, number>;
  invalidation: number;
  pattern?: string;
  ewo_divergence?: boolean;
}

export interface ElliottPayload {
  error?: string;
  bars: number;
  atr: number;
  zigzag_threshold: number;
  price: number;
  ewo: number;
  primary: ElliottScenario | null;
  alternates: ElliottScenario[];
  feed_offline: boolean;
}

export const api = {
  dashboard: () => req<DashboardPayload>("/api/dashboard"),
  web: (symbol: string) => req<WebPayload>(`/api/web/${symbol}`),
  elliott: (symbol: string, atrMult = 3) =>
    req<ElliottPayload>(`/api/elliott/${symbol}?atr_mult=${atrMult}`),
  cas: () => req<CasOverview>("/api/cas"),
  casIndex: (index: string) => req<CasIndexDetail>(`/api/cas/${index}`),
  casCalibrate: (index: string) =>
    post<{ ok: boolean; calibration: CasCalibration }>(
      `/api/cas/${index}/calibrate`,
    ),
  casPollerStart: () => post<CasOverview["poller"]>("/api/cas/poller/start"),
  casPollerStop: () => post<CasOverview["poller"]>("/api/cas/poller/stop"),
  casExpiry: () => req<CasExpiryReport>("/api/cas/expiry/report"),
  casExpiryStart: () => post<CasExpiryReport>("/api/cas/expiry/start"),
  casExpiryStop: () => post<CasExpiryReport>("/api/cas/expiry/stop"),
  casExpiryScan: () => post<CasScan>("/api/cas/expiry/scan"),
  casMomentum: () => req<MomentumReport>("/api/cas/momentum/report"),
  casMomentumStart: () => post<MomentumReport>("/api/cas/momentum/start"),
  casMomentumStop: () => post<MomentumReport>("/api/cas/momentum/stop"),
  casMomentumScan: () => post<Record<string, unknown>>("/api/cas/momentum/scan"),
  casMomentumCalendar: (year: number, month: number) =>
    req<MomentumCalendar>(`/api/cas/momentum/calendar?year=${year}&month=${month}`),
  blast: () => req<BlastReport>("/api/blast/report"),
  blastArm: () => post<BlastReport>("/api/blast/arm"),
  blastDisarm: () => post<BlastReport>("/api/blast/disarm"),
  blastScan: () => post<BlastScan>("/api/blast/scan"),
  stockPlaybook: () => req<PlaybookState>("/api/stock-playbook"),
  stockPlaybookPreview: (p: {
    symbol: string; strategy: string; timeframe: string; lots: number;
  }) =>
    req<PlaybookRow>(
      `/api/stock-playbook/preview?symbol=${p.symbol}&strategy=${p.strategy}` +
        `&timeframe=${p.timeframe}&lots=${p.lots}`,
    ),
  stockPlaybookAssign: (p: {
    symbol: string; strategy: string; timeframe: string; lots: number;
  }) =>
    post<{ assigned?: PlaybookRow; error?: string }>(
      `/api/stock-playbook/assign?symbol=${p.symbol}&strategy=${p.strategy}` +
        `&timeframe=${p.timeframe}&lots=${p.lots}`,
    ),
  stockPlaybookClear: (symbol = "") =>
    post<{ assignments: number }>(
      `/api/stock-playbook/clear${symbol ? `?symbol=${symbol}` : ""}`,
    ),
  stockPlaybookEnable: (symbol: string, on: boolean) =>
    post<{ assigned?: PlaybookRow; error?: string }>(
      `/api/stock-playbook/enable?symbol=${symbol}&on=${on}`,
    ),
  stockPlaybookBacktest: (maxHold: number, periodDays = 0, symbol = "") =>
    post<{ status: string }>(
      `/api/stock-playbook/backtest?max_hold=${maxHold}&period_days=${periodDays}` +
        (symbol ? `&symbol=${symbol}` : ""),
    ),
  stockPlaybookBacktestState: () =>
    req<PlaybookBacktestState>("/api/stock-playbook/backtest"),
  indexPlaybook: () => req<IndexPlaybookState>("/api/index-playbook"),
  indexPlaybookPreview: (p: {
    symbol: string; strategy: string; timeframe: string; lots: number;
  }) =>
    req<PlaybookRow>(
      `/api/index-playbook/preview?symbol=${p.symbol}&strategy=${p.strategy}` +
        `&timeframe=${p.timeframe}&lots=${p.lots}`,
    ),
  indexPlaybookAssign: (p: {
    symbol: string; strategy: string; timeframe: string; lots: number;
  }) =>
    post<{ assigned?: PlaybookRow; error?: string }>(
      `/api/index-playbook/assign?symbol=${p.symbol}&strategy=${p.strategy}` +
        `&timeframe=${p.timeframe}&lots=${p.lots}`,
    ),
  indexPlaybookClear: (symbol = "") =>
    post<{ assignments: number }>(
      `/api/index-playbook/clear${symbol ? `?symbol=${symbol}` : ""}`,
    ),
  indexPlaybookEnable: (symbol: string, on: boolean) =>
    post<{ assigned?: PlaybookRow; error?: string }>(
      `/api/index-playbook/enable?symbol=${symbol}&on=${on}`,
    ),
  indexPlaybookBacktest: (maxHold: number, periodDays = 0, symbol = "") =>
    post<{ status: string }>(
      `/api/index-playbook/backtest?max_hold=${maxHold}&period_days=${periodDays}` +
        (symbol ? `&symbol=${symbol}` : ""),
    ),
  indexPlaybookBacktestState: () =>
    req<PlaybookBacktestState>("/api/index-playbook/backtest"),
  backtestLog: () => req<{ results: any[] }>("/api/backtest-log"),
  paperTrade: () => req<any>("/api/paper-trade"),
  paperTradeStart: () => post<{ status: string }>("/api/paper-trade/start"),
  paperTradeStop: () => post<{ status: string }>("/api/paper-trade/stop"),
  paperTradeScan: () => post<{ ok: boolean }>("/api/paper-trade/scan"),
  masterScalping: () => req<any>("/api/master-scalping"),
  masterScalpingRun: (p: { symbols: string; setups: string; days: number; timeframe: number }) =>
    post<{ status: string }>(
      `/api/master-scalping/backtest?symbols=${p.symbols}&setups=${p.setups}` +
        `&days=${p.days}&timeframe=${p.timeframe}`,
    ),
  masterScalpingState: () => req<ISWState>("/api/master-scalping/backtest"),
  masterProfit: () => req<any>("/api/master-profit"),
  masterProfitRun: (p: { symbols: string; setups: string; days: number; timeframe: number }) =>
    post<{ status: string }>(
      `/api/master-profit/backtest?symbols=${p.symbols}&setups=${p.setups}` +
        `&days=${p.days}&timeframe=${p.timeframe}`,
    ),
  masterProfitState: () => req<ISWState>("/api/master-profit/backtest"),
  intradaySwing: () => req<ISWMeta>("/api/intraday-swing"),
  intradaySwingRun: (p: { symbols: string; setups: string; days: number; timeframe: number }) =>
    post<{ status: string }>(
      `/api/intraday-swing/backtest?symbols=${p.symbols}&setups=${p.setups}` +
        `&days=${p.days}&timeframe=${p.timeframe}`,
    ),
  intradaySwingState: () => req<ISWState>("/api/intraday-swing/backtest"),
  // --- Swing Trade (NIFTY-50, "Complete Guide to Swing Trading") ---
  swingScreener: (p: {
    min_score: number; near_high_pct: number; base_max_width: number;
    vol_mult: number; rs_min: number;
  }) =>
    req<any>(
      `/api/swing/screener?min_score=${p.min_score}&near_high_pct=${p.near_high_pct}` +
        `&base_max_width=${p.base_max_width}&vol_mult=${p.vol_mult}&rs_min=${p.rs_min}`,
    ),
  swingBacktest: (p: Record<string, string | number>) =>
    post<{ status: string }>(
      "/api/swing/backtest?" +
        Object.entries(p).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&"),
    ),
  swingBacktestState: () => req<any>("/api/swing/backtest"),
  // --- Master Swing (Alan Farley 7-Bells, NIFTY-50) ---
  masterSwingScan: (p: { setups: string; min_rr: number; target_r: number; spike_vol_mult: number }) =>
    req<any>(
      `/api/master-swing/scan?setups=${encodeURIComponent(p.setups)}&min_rr=${p.min_rr}` +
        `&target_r=${p.target_r}&spike_vol_mult=${p.spike_vol_mult}`,
    ),
  masterSwingBacktest: (p: Record<string, string | number>) =>
    post<{ status: string }>(
      "/api/master-swing/backtest?" +
        Object.entries(p).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&"),
    ),
  masterSwingBacktestState: () => req<any>("/api/master-swing/backtest"),
  // --- Count-Back Line (Bulkowski, NIFTY-50) ---
  cblScan: (p: { sma_filter: boolean; exit_mode: string; target_r: number }) =>
    req<any>(`/api/cbl/scan?sma_filter=${p.sma_filter}&exit_mode=${p.exit_mode}&target_r=${p.target_r}`),
  cblBacktest: (p: Record<string, string | number | boolean>) =>
    post<{ status: string }>(
      "/api/cbl/backtest?" +
        Object.entries(p).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&"),
    ),
  cblBacktestState: () => req<any>("/api/cbl/backtest"),
  stockTrendMaster: () => req<STMUniverse>("/api/stock-trend-master"),
  stockTrendMasterRun: (p: { preset: string; days: number; include_volatile: boolean }) =>
    post<{ status: string }>(
      `/api/stock-trend-master/backtest?preset=${p.preset}&days=${p.days}&include_volatile=${p.include_volatile}`,
    ),
  stockTrendMasterState: () => req<STMState>("/api/stock-trend-master/backtest"),
  trendMaster: () =>
    req<{ presets: TMPreset[]; coverage: TMCoverage[] }>("/api/trend-master"),
  trendMasterRun: (p: { symbols: string; presets: string; days: number }) =>
    post<{ status: string }>(
      `/api/trend-master/backtest?symbols=${p.symbols}&presets=${p.presets}&days=${p.days}`,
    ),
  trendMasterState: () => req<TMState>("/api/trend-master/backtest"),
  masterOptions: (q = "") => req<MasterCatalog>(`/api/master-options${q}`),
  masterBacktestRun: (p: { symbol: string; period: string; cycle: string }) =>
    post<{ status: string }>(
      `/api/master-options/backtest?symbol=${p.symbol}&period=${p.period}&cycle=${p.cycle}`,
    ),
  masterBacktestState: () =>
    req<MasterBacktestState>("/api/master-options/backtest"),
  masterOption: (key: string, symbol = "NIFTY") =>
    req<MasterDetail>(
      `/api/master-options/${encodeURIComponent(key)}?symbol=${symbol}`,
    ),
  mcx: () => req<McxPayload>("/api/mcx"),
  strategy: (symbol: string) =>
    req<StrategyPayload>(`/api/strategy/${encodeURIComponent(symbol)}`),
  candles: (symbol: string) =>
    req<{ symbol: string; candles: Candle[] }>(
      `/api/candles/${encodeURIComponent(symbol)}`,
    ),
  prebuilt: () => req<PrebuiltPayload>("/api/prebuilt"),
  backtest: () => req<BacktestState>("/api/backtest"),
  coverage: () => req<DataCoverage>("/api/backtest/data"),
  runBacktest: (p: {
    symbols: string;
    lots: number;
    costs: number;
    period: string;
    days?: number;
  }) =>
    post<{ status: string }>(
      `/api/backtest?symbols=${encodeURIComponent(p.symbols)}&lots=${p.lots}` +
        `&costs=${p.costs}&period=${encodeURIComponent(p.period)}&days=${p.days ?? 0}`,
    ),
  start: () => post<{ mode: BotMode }>("/api/bot/start"),
  stop: () => post<{ mode: BotMode }>("/api/bot/stop"),
  closeAll: () => post<{ ok: boolean; stats: BotStats }>("/api/bot/close-all"),
};

/* --- formatting ---------------------------------------------------------- */

const inr = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

/** ₹ with Indian digit grouping and an explicit sign for non-zero values. */
export function money(n: number, sign = false): string {
  const s = sign && n > 0 ? "+" : n < 0 ? "-" : "";
  return `${s}₹${inr.format(Math.abs(n))}`;
}

export function num(n: number, dp = 2): string {
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: dp,
    minimumFractionDigits: dp,
  });
}

export function pct(n: number, dp = 2): string {
  return `${n > 0 ? "+" : ""}${n.toFixed(dp)}%`;
}

export function tone(n: number): "up" | "down" | "flat" {
  return n > 0 ? "up" : n < 0 ? "down" : "flat";
}

/** IST wall-clock time, independent of the browser's timezone. */
export function istNow(): Date {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60_000;
  return new Date(utc + 5.5 * 3600_000);
}

/** NSE cash-session check: Mon–Fri, 09:15–15:30 IST. */
export function marketOpen(d: Date = istNow()): boolean {
  const day = d.getDay();
  if (day === 0 || day === 6) return false;
  const mins = d.getHours() * 60 + d.getMinutes();
  return mins >= 9 * 60 + 15 && mins <= 15 * 60 + 30;
}

export function greeting(d: Date = istNow()): string {
  const h = d.getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

export function clockTime(ts: number): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/* --- Macro Desk (backend/macro_desk/api.py) ------------------------------- */

/** One agent's latest published result  <- macro_desk.core.Output */
export interface MacroOutput {
  agent: string;
  status: "ok" | "no_data" | "error" | "idle" | "waiting";
  summary: string;
  /** -1..+1 bias for Indian equities; null when the job isn't directional. */
  signal: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
  sources: string[];
  missing: string[];
  /** shared agent contract: 0..1 certainty, view horizon, reason codes */
  confidence: number | null;
  horizon: string;
  reasons: string[];
  ts: number;
  elapsed_ms: number;
}

export interface MacroAgent {
  id: string;
  name: string;
  layer: 1 | 2 | 3;
  task: string;
  interval: number;
  uses_llm: boolean;
  family: string;
  busy: boolean;
  last_start: number | null;
  output: MacroOutput | null;
}

export interface MacroNews {
  id: string;
  title: string;
  link: string;
  source: string;
  published: number;
  category: "india" | "global" | "geo" | "cbank";
  impact: "high" | "medium" | "low" | null;
  direction: -1 | 0 | 1 | null;
  reason: string;
  classified_by: string;
  symbols: string[];
  sectors: string[];
  decoded_by: string;
}

export interface MacroObjection {
  level: "hard" | "soft";
  text: string;
}

export interface MacroIdea {
  id: string;
  ts: number;
  kind: "index" | "stock" | "structure";
  symbol: string;
  side: "LONG" | "SHORT" | "NEUTRAL";
  instrument: string;
  entry_ref: number;
  stop: number;
  target: number;
  conviction: number;
  /** MasterOption structures carry their catalogue numbers here */
  context?: { max_loss?: number; max_profit?: number; margin?: number; structure_key?: string; expiry?: string };
  reasons: string[];
  agent: string;
  status: "proposed" | "challenged" | "sized" | "approved" | "rejected" | "opened" | "expired";
  status_note: string;
  objections: MacroObjection[];
  risk: {
    ok?: boolean;
    qty?: number;
    lots?: number | null;
    risk_rupees?: number;
    notes?: string[];
    basis?: string;
  };
  decision: { verdict?: string; final_conviction?: number; reason?: string };
  trade_id: string | null;
}

export interface MacroTrade {
  id: string;
  kind: "index" | "stock" | "crypto" | "structure" | "sniper" | "crypto_ema";
  symbol: string;
  side: "LONG" | "SHORT";
  instrument: string;
  qty: number;
  lots: number | null;
  entry: number;
  entry_ts: number;
  stop: number;
  target: number;
  status: "OPEN" | "CLOSED";
  ltp: number;
  pnl: number;
  exit: number | null;
  exit_ts: number | null;
  exit_reason: string;
  basis: string;
  agent: string;
  /** options-priced index trades: per-leg premiums, net premium, expiry, premium stop % */
  expiry?: string;
  opt_entry?: number;
  opt_mark?: number;
  opt_exit?: number | null;
  opt_legs_entry?: { strike: number; type: string; side: "BUY" | "SELL"; px: number }[];
  opt_stop_pct?: number;
  /** EMA Agent trades: exit at -stop_rs / +target_rs of running P&L (owner, 22 Oct 2026); no_stop when switched off */
  no_stop?: boolean;
  stop_rs?: number | null;
  target_rs?: number | null;
  hold_until?: string;
  signal?: string;
  cycle?: string;
  /** crypto desk only: Delta fills (USD), P&L in USD, contracts */
  pricing?: string;
  fill_entry?: number;
  fill_mark?: number;
  fill_exit?: number;
  pnl_usd?: number;
  contracts?: number;
  peak_pnl?: number;
}

export interface MacroState {
  running: boolean;
  started_at: number | null;
  server_ts: number;
  broker_live: boolean;
  sniper_paused?: boolean;
  llm: { available: boolean; detail: string; last_error: string; per_min: number };
  config: {
    paper_capital: number;
    risk_pct: number;
    daily_loss_pct: number;
    regime_threshold: number;
  };
  agents: MacroAgent[];
  news: MacroNews[];
  news_unclassified: number;
  ideas: MacroIdea[];
  paper_open: MacroTrade[];
  paper_closed: MacroTrade[];
}

/** One option leg from the chain collectors (NSE / broker) + computed greeks. */
export interface ChainLeg {
  ltp?: number | null; chg?: number | null; oi?: number | null; chg_oi?: number | null;
  iv?: number | null; vol?: number | null; bid?: number | null; ask?: number | null;
  delta?: number; gamma?: number; theta?: number; vega?: number;
}

export interface ChainView {
  symbol: string;
  available: boolean;
  source?: string;
  ts?: number;
  spot?: number;
  expiry?: string;
  live?: boolean;
  atm?: number;
  pcr?: number | null;
  atm_iv?: number | null;
  max_pain?: number | null;
  rows?: { k: number; ce: ChainLeg; pe: ChainLeg }[];
}

/** Agentic Charts: one index/timeframe as the chart analyst sees it (Dhan bars). */
export interface ChartBar { t: number; o: number; h: number; l: number; c: number }
export interface SetupStats { n: number; win_rate?: number; expectancy_r?: number; profit_factor?: number | null }
export interface PlaybookEntry {
  pattern: string; dir: number; status: "EDGE" | "FADED" | "NO EDGE" | "INSUFFICIENT";
  train: SetupStats; test: SetupStats; all: SetupStats;
}
export interface ChartsView {
  symbol: string;
  tf: string;
  available: boolean;
  view: {
    bars: ChartBar[]; ema20: number[]; ema50: number[]; support: number[]; resistance: number[];
    marks: { t: number; setup: string; dir: number; edge: boolean }[];
  } | null;
  read: MacroOutput | null;
  playbook: Record<string, Record<string, PlaybookEntry>>;
  learned_at: number | null;
}

/**
 * Desk writes (start / stop / run) carry the engine's write token when the
 * server has one (OE_MACRO_API_TOKEN). The token is asked for once and kept in
 * this browser only; a rejected token is forgotten and asked for again.
 */
async function deskWrite<T>(path: string): Promise<T> {
  const KEY = "oe_macro_write_token";
  const send = (token: string | null) =>
    fetch(path, { method: "POST", headers: { Accept: "application/json", ...(token ? { "X-OE-Token": token } : {}) } });
  let token: string | null = null;
  try { token = localStorage.getItem(KEY); } catch { /* storage blocked */ }
  let res = await send(token);
  if (res.status === 401) {
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    token = window.prompt("This action needs the Macro Desk write token:");
    if (!token) throw new Error("write token required");
    res = await send(token);
    if (res.ok) { try { localStorage.setItem(KEY, token); } catch { /* ignore */ } }
  }
  if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

export interface MasterStrategyPosition {
  symbol: string; lane: string; pnl: number; pnl_pct: number;
  opened: number; expiry: string; days_left: number; margin: number; legs: number;
}
export interface MasterStrategyRow {
  key: string; name: string; outlook: string; volatility: string; family: string; legs: number;
  eligible: boolean; blocked_reason: string;
  status: "ACTIVE" | "WAITING" | "READY";
  positions: MasterStrategyPosition[];
  trades: number; wins: number; win_rate: number | null; pnl: number;
}
export interface MasterStrategyBoard {
  total: number; eligible: number; active: number; since: string; target: number;
  strategies: MasterStrategyRow[];
}

export const macroApi = {
  strategies: () => req<MasterStrategyBoard>("/api/live-structure/strategies"),
  state: () => req<MacroState>("/api/macro-desk/state"),
  chain: (symbol: string, n = 12) => req<ChainView>(`/api/macro-desk/chain/${symbol}?n=${n}`),
  charts: (symbol: string, tf = "5m") => req<ChartsView>(`/api/macro-desk/charts/${symbol}?tf=${tf}`),
  start: () => deskWrite<{ running: boolean }>("/api/macro-desk/start"),
  stop: () => deskWrite<{ running: boolean }>("/api/macro-desk/stop"),
  run: (agent: string) => deskWrite<{ queued: boolean }>(`/api/macro-desk/run/${agent}`),
  sniperPause: () => deskWrite<{ paused: boolean }>("/api/macro-desk/sniper/pause"),
  sniperResume: () => deskWrite<{ paused: boolean }>("/api/macro-desk/sniper/resume"),
  exitTrade: (id: string) => deskWrite<{ closed: boolean; detail: string }>(`/api/macro-desk/exit/${id}`),
};
